import { useState } from "react";
import { useInitiateOAuthFlowMutation } from "services/quickbooks-api";
import { useToken } from "utils/context";

// localStorage keys
const QB_RECONNECT_FLAG = "qbIsReconnecting";
const QB_RETURN_PATH = "qbReconnectReturnTo";

/**
 * hook for handling QuickBooks reconnection when tokens expire
 */
export const useQuickBooksReconnect = () => {
  const [isReconnecting, setIsReconnecting] = useState(false);
  const { accessToken } = useToken();
  const [initiateOAuthFlow] = useInitiateOAuthFlowMutation();

  /**
   * checks if an API error is a QuickBooks auth error and initiates reconnection
   */
  const handleApiError = async (error: unknown): Promise<boolean> => {
    const errorString = JSON.stringify(error);
    const isQuickBooksAuthError = errorString.includes("invalid_grant");

    if (!isQuickBooksAuthError) return false;

    return await startReconnection();
  };

  /**
   * initiates the QuickBooks reconnection flow
   * stores the current URL to return after reconnection
   */
  const startReconnection = async (): Promise<boolean> => {
    if (!accessToken) return false;
    if (isReconnecting) return true;

    try {
      setIsReconnecting(true);

      // store current location to return to after reconnection
      const returnPath = window.location.pathname + window.location.search;

      // set return path or default to settings page
      localStorage.setItem(
        QB_RETURN_PATH,
        returnPath && returnPath.length > 1 ? returnPath : "/settings",
      );

      // mark this as a reconnect flow
      localStorage.setItem(QB_RECONNECT_FLAG, "true");

      // call the initiate OAuth flow endpoint
      const response = await initiateOAuthFlow().unwrap();

      if (!response?.url) throw new Error("No redirect URL in response");

      // redirect to the QuickBooks auth page
      window.location.href = response.url;
      return true;
    } catch (error) {
      // clean up localStorage if there was an error
      localStorage.removeItem(QB_RECONNECT_FLAG);
      setIsReconnecting(false);
      return false;
    }
  };

  return {
    isReconnecting,
    handleApiError,
    startReconnection,
  };
};

export default useQuickBooksReconnect;
